import { useEffect, useRef, useState, type PointerEvent } from "react";

interface UseScratchCanvasInput {
  // Changes for every new card, so the foil is repainted.
  cardId: number;
  isOpen: boolean;
  onReveal: () => void;
}

const BRUSH = 46;
// Share of the foil that must be scratched off before the card opens.
const REVEAL_AT = 0.5;
const SPECKS = 22;

// The scratch-off foil: a canvas painted from the theme tokens (warm grey
// base with a soft sheen and a few specks), erased along the pointer path.
// Lifting the pointer with more than half the foil gone reveals the card.
export function useScratchCanvas({
  cardId,
  isOpen,
  onReveal,
}: UseScratchCanvasInput) {
  const initialState = { scratchedCardId: -1 };
  const [state, setState] = useState(initialState);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const isDownRef = useRef(false);
  const lastPointRef = useRef<[number, number] | null>(null);

  // Paint the foil for each new card, and again whenever the card resizes
  // (ResizeObserver also fires once when it starts observing).
  useEffect(() => {
    const canvas = canvasRef.current;
    const card = cardRef.current;
    if (isOpen || !canvas || !card) return;

    function paint() {
      const context = canvas?.getContext("2d");
      if (!canvas || !card || !context || isDownRef.current) return;
      const box = card.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.round(box.width));
      const height = Math.max(1, Math.round(box.height));
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.globalCompositeOperation = "source-over";

      const styles = getComputedStyle(canvas);
      const base = styles.getPropertyValue("--line").trim();
      const shade = styles.getPropertyValue("--ink-muted").trim();
      context.globalAlpha = 1;
      context.fillStyle = base;
      context.fillRect(0, 0, width, height);
      const sheen = context.createLinearGradient(0, 0, width, height);
      sheen.addColorStop(0, shade);
      sheen.addColorStop(0.5, "white");
      sheen.addColorStop(1, shade);
      context.globalAlpha = 0.35;
      context.fillStyle = sheen;
      context.fillRect(0, 0, width, height);
      context.globalAlpha = 0.4;
      context.fillStyle = "white";
      for (let index = 0; index < SPECKS; index++) {
        context.fillRect(Math.random() * width, Math.random() * height, 2, 2);
      }
      context.globalAlpha = 1;
    }

    const observer = new ResizeObserver(paint);
    observer.observe(card);
    return () => observer.disconnect();
  }, [cardId, isOpen]);

  const pointFrom = (event: PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return [event.clientX - box.left, event.clientY - box.top] as [
      number,
      number,
    ];
  };

  const scratchAt = (event: PointerEvent<HTMLCanvasElement>) => {
    const context = event.currentTarget.getContext("2d");
    if (!context) return;
    const point = pointFrom(event);
    const last = lastPointRef.current;
    context.globalCompositeOperation = "destination-out";
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = BRUSH;
    context.beginPath();
    if (last) {
      context.moveTo(last[0], last[1]);
      context.lineTo(point[0], point[1]);
      context.stroke();
    } else {
      context.arc(point[0], point[1], BRUSH / 2, 0, Math.PI * 2);
      context.fill();
    }
    lastPointRef.current = point;
  };

  // Share of sampled pixels that are (nearly) transparent.
  const scratchedShare = (canvas: HTMLCanvasElement) => {
    const context = canvas.getContext("2d");
    if (!context) return 0;
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    let cleared = 0;
    let sampled = 0;
    for (let index = 3; index < pixels.length; index += 160) {
      sampled++;
      if (pixels[index] < 25) cleared++;
    }
    return sampled ? cleared / sampled : 0;
  };

  const onPointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    if (isOpen) return;
    isDownRef.current = true;
    setState((state) => ({ ...state, scratchedCardId: cardId }));
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Capture is a nicety; scratching still works without it.
    }
    scratchAt(event);
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (isDownRef.current && !isOpen) scratchAt(event);
  };

  const onPointerEnd = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!isDownRef.current) return;
    isDownRef.current = false;
    lastPointRef.current = null;
    if (scratchedShare(event.currentTarget) > REVEAL_AT) onReveal();
  };

  return {
    canvasRef,
    cardRef,
    // The "Scratch here" hint hides once this card has been touched.
    showHint: !isOpen && state.scratchedCardId !== cardId,
    onPointerDown,
    onPointerMove,
    onPointerEnd,
  };
}
